/**
 * PM-6 — tenant_admin override confirm dialog (PM-D5 + PM-D18).
 *
 * The override path is a privileged exception to the no-override rule
 * (PM-D4): a `tenant_admin` MAY commit `override_block` rows — those whose
 * incoming type/coords/park differ from an EXISTING turbine — through a
 * prominent warning + an explicit confirm. Confirming sets
 * `allowOverride:true` on the commit body. The BE re-enforces the role
 * server-side and audits every overriding change with a before/after diff
 * (PM-D18); this dialog SHOWS that diff so the admin sees exactly what each
 * field changes before agreeing.
 *
 * Visibility is the CALLER's job (Step5Commit only mounts this for
 * `tenant_admin`); a non-admin never reaches this surface — they see only the
 * block-and-fix flow.
 */
import { type ReactElement } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { OverrideRowDiff } from "./overrideDiff.js";
import { TriangleAlertIcon } from "@aeriosiq/ui/icons";

export interface OverrideConfirmDialogProps {
  open: boolean;
  /** The included `override_block` rows + their per-field diffs (PM-D18). */
  rows: OverrideRowDiff[];
  pending?: boolean;
  /** Sets `allowOverride:true` and commits. */
  onConfirm: () => void;
  onCancel: () => void;
}

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v.length > 0 ? v : "—";
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return JSON.stringify(v);
}

export function OverrideConfirmDialog({
  open,
  rows,
  pending,
  onConfirm,
  onCancel,
}: OverrideConfirmDialogProps): ReactElement {
  const turbineCount = rows.length;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      aria-labelledby="override-confirm-title"
      data-testid="override-confirm-dialog"
    >
      <DialogTitle
        id="override-confirm-title"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <TriangleAlertIcon color="warning" />
        Override existing turbine data?
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" icon={<TriangleAlertIcon />} sx={{ mb: 2 }}>
          <AlertTitle>
            This overwrites good data — it cannot be undone
          </AlertTitle>
          This will overwrite existing turbine data for{" "}
          <strong>
            {turbineCount} turbine{turbineCount === 1 ? "" : "s"}
          </strong>
          . Every change is audit-logged with a before/after record (PM-D18).
          Only proceed if you are certain the incoming values are correct.
        </Alert>

        <Stack spacing={2}>
          {rows.map((row) => (
            <Box
              key={row.rowIndex}
              data-testid={`override-row-${String(row.rowIndex)}`}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <Typography variant="subtitle2">{row.label}</Typography>
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label="will change"
                />
              </Stack>
              {row.diffs.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Existing turbine differs from the sheet.
                </Typography>
              ) : (
                <Table
                  size="small"
                  aria-label={`Changes for ${row.label}`}
                  sx={{ "& td, & th": { py: 0.5 } }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Current (stored)</TableCell>
                      <TableCell>Incoming (sheet)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.diffs.map((d) => (
                      <TableRow key={d.field}>
                        <TableCell>{d.field}</TableCell>
                        <TableCell sx={{ color: "text.secondary" }}>
                          {renderValue(d.current)}
                        </TableCell>
                        <TableCell
                          sx={{ color: "warning.main", fontWeight: 600 }}
                        >
                          {renderValue(d.incoming)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={pending === true}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="warning"
          disabled={pending === true || turbineCount === 0}
          aria-label="Confirm: override and import"
          {...(pending === true
            ? { startIcon: <CircularProgress size={14} color="inherit" /> }
            : {})}
          data-testid="override-confirm-button"
        >
          {pending === true
            ? "Importing…"
            : `Override ${String(turbineCount)} turbine${
                turbineCount === 1 ? "" : "s"
              } and import`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
